import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const SalesChart = ({ data, chartType = 'bar', timeRange = '7d' }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(value);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    if (timeRange === '7d') {
      return date?.toLocaleDateString('en-US', { weekday: 'short' });
    } else if (timeRange === '30d') {
      return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
      return date?.toLocaleDateString('en-US', { month: 'short' });
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
          <p className="text-sm font-medium text-popover-foreground mb-1">
            {formatDate(label)}
          </p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-sm text-popover-foreground">
                {entry?.name}: {formatCurrency(entry?.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Sales Analytics</h3>
            <p className="text-sm text-muted-foreground">Revenue trends over time</p>
          </div>
          <div className="flex items-center space-x-2">
            <select
              className="text-sm border border-border rounded-md px-3 py-1 bg-background text-foreground"
              value={timeRange}
              onChange={() => {}}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 3 months</option>
            </select>
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="w-full h-80" aria-label="Sales Analytics Chart">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'bar' ? (
              <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis
                  tickFormatter={formatCurrency}
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="sales"
                  fill="var(--color-primary)"
                  radius={[4, 4, 0, 0]}
                  name="Sales"
                />
              </BarChart>
            ) : (
              <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis
                  tickFormatter={formatCurrency}
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke="var(--color-primary)"
                  strokeWidth={3}
                  dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: 'var(--color-primary)', strokeWidth: 2 }}
                  name="Sales"
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground">
              {formatCurrency(data?.reduce((sum, item) => sum + item?.sales, 0))}
            </p>
            <p className="text-sm text-muted-foreground">Total Revenue</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground">
              {formatCurrency(data?.reduce((sum, item) => sum + item?.sales, 0) / data?.length)}
            </p>
            <p className="text-sm text-muted-foreground">Average Daily</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground">
              {Math.max(...data?.map(item => item?.sales)) > 0 ? '+' : ''}
              {(((data?.[data?.length - 1]?.sales || 0) - (data?.[0]?.sales || 0)) / (data?.[0]?.sales || 1) * 100)?.toFixed(1)}%
            </p>
            <p className="text-sm text-muted-foreground">Growth Rate</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesChart;